import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth context hook
export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setIsAuthenticated(false);
      setLoading(false);
      return false;
    }

    try {
      await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAuthenticated(true);
      setLoading(false);
      return true;
    } catch (error) {
      localStorage.removeItem('adminToken');
      setIsAuthenticated(false);
      setLoading(false);
      return false;
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem('adminToken', response.data.access_token);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    navigate('/admin/login');
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return { isAuthenticated, loading, login, logout, checkAuth, getAuthHeader };
};

// Blog API hooks
export const useBlogApi = () => {
  const getAuthHeader = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Public endpoints
  const getPublishedPosts = async (page = 1, limit = 10, category = null) => {
    const params = { page, limit };
    if (category) params.category = category;
    const response = await axios.get(`${API}/blog/posts`, { params });
    return response.data;
  };

  const getPostBySlug = async (slug) => {
    const response = await axios.get(`${API}/blog/posts/${slug}`);
    return response.data;
  };

  const getCategories = async () => {
    const response = await axios.get(`${API}/blog/categories`);
    return response.data.categories;
  };

  // Admin endpoints
  const adminGetPosts = async (page = 1, limit = 20, status = null) => {
    const params = { page, limit };
    if (status) params.status = status;
    const response = await axios.get(`${API}/admin/blog/posts`, {
      params,
      headers: getAuthHeader()
    });
    return response.data;
  };

  const adminGetPost = async (postId) => {
    const response = await axios.get(`${API}/admin/blog/posts/${postId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  };

  const createPost = async (postData) => {
    const response = await axios.post(`${API}/admin/blog/posts`, postData, {
      headers: getAuthHeader()
    });
    return response.data;
  };

  const updatePost = async (postId, postData) => {
    const response = await axios.put(`${API}/admin/blog/posts/${postId}`, postData, {
      headers: getAuthHeader()
    });
    return response.data;
  };

  const deletePost = async (postId) => {
    const response = await axios.delete(`${API}/admin/blog/posts/${postId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/admin/upload`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  };

  return {
    getPublishedPosts,
    getPostBySlug,
    getCategories,
    adminGetPosts,
    adminGetPost,
    createPost,
    updatePost,
    deletePost,
    uploadImage
  };
};
